/**
 * Leave display units — the ONE place a balance becomes text.
 *
 * See DESIGN-leave-units-and-entitlement.md (D1). The rules, in order of how much
 * damage breaking them does:
 *
 * 1. MINUTES ARE CANONICAL. Hours are rendered from the integer `*_minutes` columns,
 *    never by multiplying a float day count by a day length. `16.5 * 450` is not the
 *    number the server stored, it is a number that happens to look like it, and the
 *    two drift apart the moment anything rounds.
 *
 * 2. DAYS MODE RENDERS THE STORED FLOAT, UNCHANGED. It does NOT re-derive days from
 *    minutes ÷ basis. Every employee is on days by default, so re-deriving would
 *    silently move numbers on screens nobody asked us to touch — for a display-only
 *    feature. The float and the minutes agree; when they don't, the float is what the
 *    employee has been looking at all year.
 *
 * 3. NO MINUTES => FALL BACK TO DAYS. A row that predates the minutes backfill has
 *    NULL minutes. Hours mode then degrades to today's behaviour rather than to "0h"
 *    or a blank. An un-migrated row must never read as "no leave left".
 *
 * 4. NEGATIVES ARE LEGITIMATE. An overdrawn leaver has a negative balance; the
 *    reconciliation test reports them rather than failing. Format them, don't clamp.
 *
 * The unit is the OWNING EMPLOYEE's (`display_unit` on their user / allowance payload),
 * not the viewer's: an admin looking at a mornings-only part-timer sees that person's
 * balance the way that person sees it. "28 days" of 3-hour days is only honest in hours.
 */

export const DAYS = 'days';
export const HOURS = 'hours';

/** Normalise whatever the API gave us into 'days' | 'hours'. Unknown => days. */
export function normUnit(unit) {
  return String(unit || '').toLowerCase() === HOURS ? HOURS : DAYS;
}

/**
 * Pull the unit + minutes out of any of our balance payload shapes.
 * The API is not consistent (`total_allowance` vs `total`, `allowance_remaining` vs
 * `remaining`), so callers pass the pieces explicitly rather than a payload.
 */
function pick(opts) {
  const o = opts || {};
  const minutes = o.minutes === undefined ? null : o.minutes;
  const basis = o.basis === undefined ? null : o.basis;
  return { unit: normUnit(o.unit), minutes, basis };
}

/** True when we have real minutes to render hours from. 0 is a valid balance. */
function hasMinutes(m) {
  return m !== null && m !== undefined && Number.isFinite(Number(m));
}

/**
 * Resolve the unit for a WHOLE CARD, given every minutes value that card needs.
 *
 * Call this once per card and pass the result to every formatLeave in it. Without it,
 * each figure falls back independently and a half-backfilled row renders
 * "16.5 of 255h" — two units, one sentence. Falling the whole card back to days is
 * always coherent, because the day floats are never NULL.
 *
 * Use it anywhere a card shows more than one number. A single-figure display can just
 * pass the unit straight to formatLeave.
 */
export function resolveUnit(unit, ...minuteValues) {
  if (normUnit(unit) !== HOURS) return DAYS;
  return minuteValues.every(hasMinutes) ? HOURS : DAYS;
}

/** Trim a float day count for display: 26.0 -> "26", 16.5 -> "16.5", 16.25 -> "16.25". */
export function trimDays(n) {
  const v = Number(n || 0);
  // Round to 2dp to kill float fuzz (16.499999999), then drop trailing zeros.
  return String(Math.round(v * 100) / 100);
}

/**
 * Integer minutes -> "49h 30m". Never takes a float day count.
 *   0    -> "0h"       (a spent balance is 0h, not blank)
 *   120  -> "2h"       (no dangling "0m")
 *   45   -> "45m"
 *   -90  -> "-1h 30m"  (overdrawn leaver — real, keep the sign)
 */
export function formatMinutes(minutes) {
  const total = Math.round(Number(minutes) || 0);
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(total);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h && m) return `${sign}${h}h ${m}m`;
  if (h) return `${sign}${h}h`;
  if (m) return `${sign}${m}m`;
  return '0h';
}

/**
 * The main formatter. Give it the days float the API already sends, plus the minutes
 * and the employee's unit; get back the string to render.
 *
 *   formatLeave(16.5, { unit: 'days' })                         -> "16.5 days"
 *   formatLeave(16.5, { unit: 'hours', minutes: 4455 })         -> "74h 15m"
 *   formatLeave(1,    { unit: 'days' })                         -> "1 day"
 *   formatLeave(16.5, { unit: 'hours' })  // no minutes yet     -> "16.5 days"
 *
 * `withUnit: false` drops the trailing "days"/"day" word (hours keep their h/m — the
 * suffix IS the number there), for call sites that print their own label.
 */
export function formatLeave(days, opts) {
  const { unit, minutes } = pick(opts);
  const withUnit = !opts || opts.withUnit !== false;

  if (unit === HOURS && hasMinutes(minutes)) {
    return formatMinutes(minutes);
  }

  // No days figure either => we know nothing. Render a dash, NOT "0 days": a missing
  // balance and a spent balance are different facts, and only one of them means
  // "you have no leave left". (Rule 3, applied to the days side.)
  if (days === null || days === undefined || !Number.isFinite(Number(days))) {
    return '—';
  }

  const text = trimDays(days);
  if (!withUnit) return text;
  return `${text} ${Number(days) === 1 ? 'day' : 'days'}`;
}

/**
 * "26 of 34 days" / "195h of 255h" — the "X of Y" idiom, which shows up on the
 * dashboard card and YourSetupCard and got written slightly differently each time.
 * Both figures share one unit; the day word lands once, at the end.
 */
export function formatLeavePair(days, ofDays, opts) {
  const { unit, minutes } = pick(opts);
  const ofMinutes = opts ? opts.ofMinutes : null;

  if (unit === HOURS && hasMinutes(minutes) && hasMinutes(ofMinutes)) {
    return `${formatMinutes(minutes)} of ${formatMinutes(ofMinutes)}`;
  }
  return `${trimDays(days)} of ${trimDays(ofDays)} ${Number(ofDays) === 1 ? 'day' : 'days'}`;
}

/**
 * Compact badge for the wallchart, where space is a few pixels: "26d" / "195h".
 * Replaces a hard-coded "d" suffix that lied to anyone on hours.
 */
export function formatLeaveShort(days, opts) {
  const { unit, minutes } = pick(opts);
  if (unit === HOURS && hasMinutes(minutes)) {
    const total = Math.round(Number(minutes) || 0);
    const sign = total < 0 ? '-' : '';
    const abs = Math.abs(total);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    // Hours AND minutes, as everywhere else ("176h 15m"). It used to round to one decimal for room
    // ("176.3h" — which isn't 176h 18m or anything a person recognises); the Team calendar's rows
    // have had a whole line for badges since 29/10/2026 (Rob, 02/11/2026).
    if (!h && !m) return '0h';
    if (!m) return `${sign}${h}h`;
    if (!h) return `${sign}${m}m`;
    return `${sign}${h}h ${m}m`;
  }
  return `${trimDays(days)}d`;
}

/** "days" | "hours" — for column headers and field labels. */
export function unitLabel(opts) {
  return normUnit(opts && opts.unit) === HOURS ? 'hours' : 'days';
}

/**
 * Days from canonical minutes. Provided for the rare caller that genuinely needs a
 * NUMBER in days (chart bar heights, percentages) rather than text.
 * Returns null when the row has no minutes — the caller keeps using the float.
 */
export function daysFromMinutes(minutes, basis) {
  if (!hasMinutes(minutes) || !basis) return null;
  return Math.round((Number(minutes) / Number(basis)) * 1000) / 1000;
}
